import { useEffect, useMemo, useRef, useState } from 'react';
import { surgeryServices } from '../services/endpoints/surgery';
import {
  AnalyticsViewMode,
  EMPTY_SURGERY_ANALYTICS_FILTERS,
  SourceState,
  SurgeryAnalyticsFiltersQuery,
  SurgeryData,
  SurgeryQuickKpisResponse,
} from '../types';
import { surgeryChartsForMode } from '../config/analyticsCharts';
import {
  surgeryPeriodActivityChartFromQuickKpis,
  surgeryPipelineChart,
  surgeryProcedureTableRows,
  surgerySurgeonTableRows,
  surgeryTopProceduresChart,
  surgeryWaitDistributionChart,
} from '../utils/surgeryChartTransforms';

type SurgeryFetch = (
  uuidInvestigation: string,
  uuidDepartment: string,
  startDate: string,
  endDate: string,
  filters: SurgeryAnalyticsFiltersQuery,
  signal?: AbortSignal,
) => Promise<any>;

const requests = {
  quickKpis: surgeryServices.getQuickKpis,
  byProcedure: surgeryServices.getByProcedure,
  bySurgeon: surgeryServices.getBySurgeon,
  waitingTime: surgeryServices.getWaitingTimeDistribution,
  pipelineByStatus: surgeryServices.getPipelineByStatus,
} satisfies Record<string, SurgeryFetch>;

type RequestKey = keyof typeof requests;

interface SurgerySource<K extends keyof SurgeryData = keyof SurgeryData> {
  request: RequestKey;
  select?: (response: any, context: { periodLabel: string }) => SurgeryData[K];
}

const sourceTable = {
  quickKpis: {
    request: 'quickKpis',
    select: (response: SurgeryQuickKpisResponse) => response?.stats?.totals ?? null,
  },
  periodActivity: {
    request: 'quickKpis',
    select: (response: SurgeryQuickKpisResponse, { periodLabel }) =>
      surgeryPeriodActivityChartFromQuickKpis(response?.stats?.totals, periodLabel),
  },
  topProcedures: {
    request: 'byProcedure',
    select: (response) => surgeryTopProceduresChart(response?.stats?.rows, 10),
  },
  byProcedure: {
    request: 'byProcedure',
    select: (response) => surgeryProcedureTableRows(response?.stats?.rows),
  },
  bySurgeon: {
    request: 'bySurgeon',
    select: (response) => surgerySurgeonTableRows(response?.stats?.rows),
  },
  waitingTime: {
    request: 'waitingTime',
    select: (response) => surgeryWaitDistributionChart(response?.stats?.buckets),
  },
  pipelineByStatus: {
    request: 'pipelineByStatus',
    select: (response) => surgeryPipelineChart(response?.stats?.rows),
  },
} satisfies { [K in keyof SurgeryData]: SurgerySource<K> };

type EndpointState =
  | { status: 'pending' }
  | { status: 'ok'; response: any }
  | { status: 'error'; error: Error };

const SOURCES = new Map<string, SurgerySource>(Object.entries(sourceTable));

export const dedupeSurgeryRequests = (charts: { source: string }[]): RequestKey[] => {
  const keys = new Set<RequestKey>();
  charts.forEach((chart) => {
    const source = SOURCES.get(chart.source);
    if (source) {
      keys.add(source.request);
    }
  });
  return Array.from(keys);
};

const requestsForMode = (mode: AnalyticsViewMode): RequestKey[] =>
  dedupeSurgeryRequests(surgeryChartsForMode(mode));

interface UseSurgeryDataOptions {
  investigation: { uuid: string };
  departmentSelected?: string;
  startDate: string;
  endDate: string;
  mode: AnalyticsViewMode;
  filters?: SurgeryAnalyticsFiltersQuery;
  periodLabel?: string;
}

export const useSurgeryData = ({
  investigation,
  departmentSelected,
  startDate,
  endDate,
  mode,
  filters = EMPTY_SURGERY_ANALYTICS_FILTERS,
  periodLabel = '',
}: UseSurgeryDataOptions) => {
  const [endpoints, setEndpoints] = useState<Partial<Record<RequestKey, EndpointState>>>({});

  const uuidDepartment = departmentSelected || 'all';
  const filtersKey = JSON.stringify(filters);
  const filterKey = `${investigation?.uuid}|${uuidDepartment}|${startDate}|${endDate}|${filtersKey}`;

  const abortRef = useRef<AbortController | null>(null);
  const endpointsRef = useRef<Partial<Record<RequestKey, EndpointState>>>({});
  const filterKeyRef = useRef('');

  const commitEndpoints = (
    update: (
      current: Partial<Record<RequestKey, EndpointState>>,
    ) => Partial<Record<RequestKey, EndpointState>>,
  ) => {
    endpointsRef.current = update(endpointsRef.current);
    setEndpoints(endpointsRef.current);
  };

  useEffect(() => () => abortRef.current?.abort(), []);

  useEffect(() => {
    if (!investigation?.uuid || !startDate || !endDate) {
      return;
    }

    if (filterKeyRef.current !== filterKey) {
      abortRef.current?.abort();
      abortRef.current = new AbortController();
      filterKeyRef.current = filterKey;
      commitEndpoints(() => ({}));
    }

    const controller = abortRef.current as AbortController;
    const toFetch = requestsForMode(mode).filter((key) => {
      const state = endpointsRef.current[key];
      return !state || state.status === 'error';
    });
    if (!toFetch.length) {
      return;
    }
    commitEndpoints((current) =>
      toFetch.reduce((next, key) => ({ ...next, [key]: { status: 'pending' } }), current),
    );

    toFetch.forEach(async (key) => {
      try {
        const response = await requests[key](
          investigation.uuid,
          uuidDepartment,
          startDate,
          endDate,
          filters,
          controller.signal,
        );
        if (controller.signal.aborted) {
          return;
        }
        commitEndpoints((current) => ({ ...current, [key]: { status: 'ok', response } }));
      } catch (err) {
        if (!controller.signal.aborted) {
          commitEndpoints((current) => ({
            ...current,
            [key]: {
              status: 'error',
              error: err instanceof Error ? err : new Error('Unknown error'),
            },
          }));
        }
      }
    });
  }, [filterKey, mode, investigation?.uuid, uuidDepartment, startDate, endDate, filters]);

  const selectContext = useMemo(() => ({ periodLabel }), [periodLabel]);

  const data = useMemo(() => {
    return Array.from(SOURCES.entries()).reduce<Partial<SurgeryData>>(
      (built, [source, { request, select }]) => {
        const entry = endpoints[request];
        if (entry?.status !== 'ok' || !entry.response) {
          return built;
        }
        return {
          ...built,
          [source]: select ? select(entry.response, selectContext) : entry.response,
        };
      },
      {},
    );
  }, [endpoints, selectContext]);

  const sourceStates = useMemo(() => {
    const sources = new Set(surgeryChartsForMode(mode).map((chart) => chart.source));
    return Array.from(sources).reduce<Record<string, SourceState>>((states, source) => {
      const entry = SOURCES.get(source);
      if (!entry) {
        return { ...states, [source]: { status: 'ok' } };
      }
      const state: EndpointState = endpoints[entry.request] ?? { status: 'pending' };
      if (state.status === 'pending') {
        return { ...states, [source]: { status: 'pending' } };
      }
      if (state.status === 'error') {
        return { ...states, [source]: { status: 'error', error: state.error } };
      }
      return { ...states, [source]: { status: 'ok' } };
    }, {});
  }, [endpoints, mode]);

  return { data, sourceStates };
};
